import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ListChecks, Users, User as UserIcon } from 'lucide-react';
import TaskPhasePrompt from './TaskPhasePrompt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GROUPS = [
  { key: 'vencidas', label: 'Vencidas', cls: 'text-red-400' },
  { key: 'hoy', label: 'Para hoy', cls: 'text-amber-300' },
  { key: 'pendientes_agendar', label: 'Pendientes de agendar', cls: 'text-slate-300' },
  { key: 'proximas', label: 'Próximas', cls: 'text-slate-300' },
];

const fmtFecha = (t) => {
  if (!t.fecha) return 'Sin fecha';
  return t.hora ? `${t.fecha} ${t.hora}` : t.fecha;
};

const TaskRow = ({ task, onDone, onLastTaskClosed, readOnly }) => {
  const [mode, setMode] = useState(null); // 'agendar' | 'resolver' | null
  const [fecha, setFecha] = useState(task.fecha || '');
  const [hora, setHora] = useState(task.hora || '');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);
  const asset = task.asset;
  const assetLabel = asset ? (asset.codigo_npl || asset.direccion || asset.id) : task.asset_id;

  const handleAgendar = async () => {
    if (!fecha) { toast.error('Selecciona una fecha'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/assets/${task.asset_id}/tasks/${task.id}`, { fecha, hora: hora || null }, { withCredentials: true });
      toast.success('Tarea agendada');
      setMode(null);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agendar');
    } finally {
      setSaving(false);
    }
  };

  const handleResolver = async () => {
    if (!nota.trim()) { toast.error('La nota de resolución es obligatoria'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/assets/${task.asset_id}/tasks/${task.id}/complete`, { nota_resolucion: nota.trim() }, { withCredentials: true });
      toast.success('Tarea resuelta');
      setMode(null);
      onDone();
      if (r.data?.remaining_open_tasks === 0 && r.data?.asset_etapa_visual === 'en_gestion') {
        onLastTaskClosed(task.asset_id, r.data.asset_etapa_visual);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resolver');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-slate-700/60 bg-slate-800/40" data-testid={`mis-tareas-row-${task.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to={`/activos/${task.asset_id}?tab=tareas`} className="text-sm font-medium text-blue-300 hover:underline" data-testid={`mis-tareas-asset-link-${task.id}`}>
            {assetLabel}
          </Link>
          <p className="text-sm text-slate-200 mt-0.5">{task.tarea}</p>
          <p className="text-xs text-slate-400 mt-0.5">{fmtFecha(task)}{task.responsable_nombre ? ` · ${task.responsable_nombre}` : ''}</p>
        </div>
        {!mode && !readOnly && (
          <div className="flex items-center gap-2 shrink-0">
            {!task.fecha && (
              <button onClick={() => setMode('agendar')} className="text-xs font-medium px-2 py-1 rounded border border-slate-600 text-slate-200 hover:bg-slate-700" data-testid={`mis-tareas-agendar-btn-${task.id}`}>
                Agendar
              </button>
            )}
            <button onClick={() => setMode('resolver')} className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`mis-tareas-resolver-btn-${task.id}`}>
              Resolver
            </button>
          </div>
        )}
      </div>
      {mode === 'agendar' && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <input type="date" className="input-base" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid={`mis-tareas-agendar-fecha-${task.id}`} />
          <input type="time" className="input-base" value={hora} onChange={(e) => setHora(e.target.value)} />
          <button onClick={() => setMode(null)} className="text-xs px-2 py-1.5 rounded border border-slate-600 text-slate-300">Cancelar</button>
          <button onClick={handleAgendar} disabled={saving} className="text-xs font-semibold px-2 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700" data-testid={`mis-tareas-agendar-confirm-${task.id}`}>
            {saving ? 'Guardando…' : 'Guardar fecha'}
          </button>
        </div>
      )}
      {mode === 'resolver' && (
        <div className="mt-2 space-y-2">
          <textarea className="input-base w-full min-h-[50px]" placeholder="Nota de resolución" value={nota} onChange={(e) => setNota(e.target.value)} data-testid={`mis-tareas-resolver-nota-${task.id}`} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode(null)} className="text-xs px-2 py-1.5 rounded border border-slate-600 text-slate-300">Cancelar</button>
            <button onClick={handleResolver} disabled={saving} className="text-xs font-semibold px-2 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`mis-tareas-resolver-confirm-${task.id}`}>
              {saving ? 'Guardando…' : 'Confirmar resolución'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MisTareasBlock = ({ canViewTeam, readOnly }) => {
  const [scope, setScope] = useState('mine');
  const [buckets, setBuckets] = useState({ vencidas: [], hoy: [], pendientes_agendar: [], proximas: [] });
  const [loading, setLoading] = useState(false);
  const [phasePrompt, setPhasePrompt] = useState(null); // { assetId, etapaVisual } | null

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/tareas`, { params: { scope }, withCredentials: true });
      setBuckets({
        vencidas: r.data?.vencidas || [],
        hoy: r.data?.hoy || [],
        pendientes_agendar: r.data?.pendientes_agendar || [],
        proximas: r.data?.proximas || [],
      });
    } catch {
      toast.error('No se pudieron cargar tus tareas');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const total = GROUPS.reduce((acc, g) => acc + (buckets[g.key]?.length || 0), 0);

  return (
    <div className="card-base p-6" data-testid="mis-tareas-block">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <ListChecks className="w-5 h-5" style={{ color: 'var(--color-blue-accent)' }} />
          {scope === 'team' ? 'Tareas del equipo' : 'Mis tareas'} · {total}
        </h2>
        {canViewTeam && (
          <div className="flex items-center gap-2" data-testid="mis-tareas-scope-toggle">
            <button
              onClick={() => setScope('mine')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${scope === 'mine' ? 'border-blue-500 text-blue-300' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
              data-testid="mis-tareas-scope-mine-btn"
            >
              <UserIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Mis tareas
            </button>
            <button
              onClick={() => setScope('team')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${scope === 'team' ? 'border-blue-500 text-blue-300' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
              data-testid="mis-tareas-scope-team-btn"
            >
              <Users className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Equipo
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-4">Cargando…</p>
      ) : total === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4" data-testid="mis-tareas-empty">No tienes tareas pendientes.</p>
      ) : (
        <div className="space-y-5">
          {GROUPS.map((g) => {
            const list = buckets[g.key] || [];
            if (list.length === 0) return null;
            return (
              <div key={g.key} data-testid={`mis-tareas-group-${g.key}`}>
                <p className={`text-sm font-semibold mb-2 ${g.cls}`}>{g.label} · {list.length}</p>
                <div className="space-y-2">
                  {list.map((t) => <TaskRow key={t.id} task={t} onDone={fetchTasks} readOnly={readOnly} onLastTaskClosed={(assetId, etapaVisual) => setPhasePrompt({ assetId, etapaVisual })} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TaskPhasePrompt
        open={!!phasePrompt}
        assetId={phasePrompt?.assetId}
        currentEtapaVisual={phasePrompt?.etapaVisual}
        onClose={() => setPhasePrompt(null)}
        onCreateTask={() => setPhasePrompt(null)}
        onDone={() => { setPhasePrompt(null); fetchTasks(); }}
      />
    </div>
  );
};

export default MisTareasBlock;
